'use client';

import React, { RefObject } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faXmark } from '@fortawesome/pro-solid-svg-icons';

interface FilterDropdownButtonProps {
  icon: IconDefinition;
  label: string;
  isOpen: boolean;
  isDisabled: boolean;
  displayText: string;
  hasSelection: boolean;
  onClear: (e: React.MouseEvent) => void;
  onClick: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
  testId?: string;
  iconClassName?: string;
}

const FilterDropdownButton: React.FC<FilterDropdownButtonProps> = ({
  icon,
  isOpen,
  isDisabled,
  displayText,
  hasSelection,
  onClear,
  onClick,
  buttonRef,
  testId,
  iconClassName = 'text-tertiary text-xs',
}): React.ReactElement => {
  return (
    <button
      ref={buttonRef}
      className={`p-2 @[1250px]/filters:px-3.5 @[1250px]/filters:py-2 rounded-full border inline-flex items-center gap-2 ${
        isDisabled
          ? 'cursor-not-allowed opacity-50 bg-neutral-100 border-neutral-300'
          : hasSelection
            ? 'bg-neutral-100 border-neutral-300 hover:bg-neutral-200 cursor-pointer'
            : 'bg-neutral-100 border-neutral-300 hover:bg-neutral-200 cursor-pointer'
      }`}
      onClick={onClick}
      type="button"
      disabled={isDisabled}
      data-testid={testId}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label={displayText}
    >
      <Icon icon={icon} className={iconClassName} />
      <span
        className={`hidden @[1250px]/filters:inline text-sm font-semibold leading-tight whitespace-nowrap ${
          isDisabled ? 'text-neutral-400' : 'text-primary'
        }`}
      >
        {displayText}
      </span>
      {hasSelection && (
        <Icon
          icon={faXmark}
          className="text-tertiary hover:text-primary"
          size="sm"
          onClick={onClear}
        />
      )}
      <Icon
        icon={faChevronDown}
        className={`hidden @[1250px]/filters:inline text-tertiary text-xs transition-transform duration-300 ${
          isOpen ? '-rotate-90' : 'rotate-0'
        }`}
      />
    </button>
  );
};

export default FilterDropdownButton;
